import { Service, Facility, Equipment, Testimonial } from '@/lib/index';
import { IMAGES } from '@/assets/images';

export const services: Service[] = [
  {
    id: 'film-production',
    title: 'Film Production',
    description: 'End-to-end feature film and series production with world-class directors, cinematographers, and technical crew. From pre-production planning to final delivery, we bring global storytelling to life.',
    icon: 'Film',
    features: [
      'Feature films and series production',
      'Commercial and branded content',
      'Virtual production capabilities',
      'Multi-camera setups and specialty rigs',
      'International crew coordination',
      'Location scouting and management',
    ],
  },
  {
    id: 'post-production',
    title: 'Post-Production Services',
    description: 'State-of-the-art editing, color grading, sound design, and VFX coordination. Our advanced suites support 4K/8K workflows with professional reference displays and high-performance workstations.',
    icon: 'Clapperboard',
    features: [
      'Professional editing suites',
      'Advanced color grading and finishing',
      'Sound design and mixing',
      'VFX coordination and supervision',
      'High-resolution offline and online editing',
      'Secure data storage and archival',
    ],
  },
  {
    id: 'studio-rental',
    title: 'Studio Rental',
    description: 'Flexible studio spaces with professional chroma key, advanced lighting systems, and rapid set reconfiguration. Perfect for productions of all scales, from commercials to feature films.',
    icon: 'Building2',
    features: [
      'Multi-utility shooting floor with chroma key',
      'Professional lighting rigs and grip equipment',
      'Flexible set configurations',
      'Climate-controlled environment',
      'Production support facilities',
      'Equipment storage and prep areas',
    ],
  },
  {
    id: 'equipment-rental',
    title: 'Equipment Rental',
    description: 'Comprehensive inventory of cinema cameras, lenses, lighting, and grip equipment. All equipment is owned, maintained, and regularly updated to industry standards.',
    icon: 'Camera',
    features: [
      'Large-format digital cinema cameras (8K+)',
      'Premium cinema prime and zoom lenses',
      'Advanced RGB and LED lighting systems',
      'Professional grip and rigging support',
      'High-speed and specialty cameras',
      'Broadcast and cinema camera kits',
    ],
  },
  {
    id: 'creative-development',
    title: 'Creative Development',
    description: 'Collaborative script development, storyboarding, and pre-visualization services. We help shape narratives that resonate globally while maintaining artistic integrity.',
    icon: 'Lightbulb',
    features: [
      'Script consultation and development',
      'Storyboarding and pre-visualization',
      'Creative direction and concept design',
      'International market analysis',
      'Casting coordination',
      'Production design consultation',
    ],
  },
  {
    id: 'global-collaboration',
    title: 'Global Collaboration',
    description: 'Facilitating international co-productions and bringing together talent from across the globe. We specialize in creating "one-globe narratives" that transcend borders and cultures.',
    icon: 'Globe',
    features: [
      'International crew coordination',
      'Multi-territory production support',
      'Cross-cultural storytelling expertise',
      'Global distribution consultation',
      'Festival strategy and submissions',
      'International financing coordination',
    ],
  },
  {
    id: 'vfx-cgi',
    title: 'VFX & CGI Services',
    description: 'Cutting-edge visual effects and CGI services with AI technology integration. We create stunning visual effects, from subtle enhancements to full CGI environments, utilizing the latest AI-powered tools for motion tracking, rotoscoping, and procedural generation.',
    icon: 'Sparkles',
    features: [
      'Full CGI character and environment creation',
      'Green screen compositing and keying',
      'Motion tracking and match moving',
      'AI-powered rotoscoping and masking',
      'Procedural VFX generation with AI',
      '3D modeling and animation',
      'Particle effects and simulations',
      'AI-enhanced motion interpolation',
      'Real-time preview and rendering',
      'Color correction and finishing',
    ],
  },
  {
    id: 'distribution-exhibition',
    title: 'Global Distribution & Exhibition',
    description: 'Navigating the complex landscape of global distribution can be overwhelming. We make it effortless by syndicating your content to all major OTT platforms and satellite networks worldwide, ensuring your story reaches audiences across every screen and territory.',
    icon: 'Tv',
    features: [
      'OTT platform syndication (Netflix, Amazon Prime, Disney+, etc.)',
      'Satellite and cable network distribution worldwide',
      'Multi-territory licensing and rights management',
      'Digital distribution strategy and optimization',
      'Theatrical release coordination and booking',
      'Festival circuit distribution and sales',
      'Revenue maximization across all platforms',
      'Compliance with regional content regulations',
      'Delivery specifications for all major platforms',
      'Distribution analytics and performance tracking',
    ],
  },
  {
    id: 'pr-marketing',
    title: 'PR & Marketing',
    description: 'In today\'s crowded media landscape, proper placement in digital and print media is crucial for success. Our dedicated PR team ensures your film, cast, crew, and creative talent receive the visibility they deserve through strategic media campaigns, press coverage, and targeted promotional activities.',
    icon: 'Megaphone',
    features: [
      'Strategic media planning and placement',
      'Digital marketing campaigns (social media, influencer partnerships)',
      'Print media coverage and press releases',
      'Cast and crew publicity and interviews',
      'Film festival PR and red carpet coordination',
      'Premiere and special event management',
      'Trailer and poster campaign strategy',
      'Press kit development and distribution',
      'Crisis management and reputation protection',
      'Awards campaign strategy and execution',
      'Talent branding and personal PR services',
      'Media training for cast and crew',
    ],
  },
];

export const facilities: Facility[] = [
  {
    id: 'shooting-floor',
    name: 'Multi-Utility Shooting Floor',
    description: 'Professional chroma key studio with flexible lighting rigs, overhead mounting systems, and rapid set reconfiguration capabilities. Climate-controlled environment suitable for productions of all scales.',
    image: IMAGES.BIBIA_STUDIOS_MULTIUTILITY_SHOOTING_CROMA_FLOOR_1_105,
    specifications: [
      'Professional green screen/chroma key wall',
      'Ceiling-mounted lighting track system',
      'Climate-controlled environment',
      'Flexible set configuration',
      'Production support areas',
      'Equipment prep and storage access',
    ],
  },
  {
    id: 'main-editing-suite',
    name: 'Main Editing & Color Grading Suite',
    description: 'High-performance editing and grading workstation with multi-monitor setup, professional reference displays for 4K/8K monitoring, and dedicated color grading control panels.',
    image: IMAGES.BIBIA_STUDIOS_MAIN_BOSS_EDITING_ROOM_1_108,
    specifications: [
      'Multi-monitor editing workstation',
      'Professional 4K/8K reference displays',
      'Color grading control panels',
      'High-performance computing systems',
      'Professional studio monitors',
      'Acoustic treatment for audio work',
    ],
  },
  {
    id: 'editorial-workspace',
    name: 'Editorial Workspaces',
    description: 'Additional editing rooms equipped with professional-grade workstations, dual-monitor setups, and comfortable ergonomic furniture for extended editing sessions.',
    image: IMAGES.BIBIA_STUDIOS_EDITING_ROOM_GENERAL_1_100,
    specifications: [
      'Dual-monitor editing stations',
      'Ergonomic seating and desks',
      'Professional editing software suites',
      'High-speed network connectivity',
      'Secure project storage',
      'Collaborative review capabilities',
    ],
  },
  {
    id: 'executive-meeting-room',
    name: 'Executive Producer Suite',
    description: 'Premium meeting space for producers, directors, and investors. Features large conference table, presentation displays, and sophisticated ambiance for high-level discussions.',
    image: IMAGES.BIBIA_STUDIOS_BOSS_KESHHAV_S_PERSONAL_MEETING_ROOM_1_33,
    specifications: [
      'Large conference table seating 12+',
      'Large-format presentation display',
      'Professional audio/video conferencing',
      'Ambient lighting control',
      'Film memorabilia and awards display',
      'Private and soundproofed',
    ],
  },
  {
    id: 'production-office',
    name: 'Assistant Director & Production Room',
    description: 'Dedicated workspace for assistant directors and production coordinators. Equipped with workstations, planning boards, and creative collaboration areas.',
    image: IMAGES.BIBIA_STUDIOS_ASSISTANTS_DIRECTOR_PLUS_PRODUCTION_ROOM_1_110,
    specifications: [
      'Multiple workstation setups',
      'Large planning and mood boards',
      'Video conferencing capabilities',
      'Production scheduling tools',
      'Creative collaboration space',
      'Equipment and prop storage',
    ],
  },
  {
    id: 'reception-area',
    name: 'Reception & Welcome Area',
    description: 'Modern reception area with dramatic architectural lighting, curved reception desk, and cinematic ambiance. First impression space showcasing studio creative identity.',
    image: IMAGES.BIBIA_STUDIOS_RECEPTION_WELCOME_AREA_1_32,
    specifications: [
      'Dramatic LED ambient lighting',
      'Modern curved reception desk',
      'Film poster displays',
      'Comfortable waiting area',
      'Large presentation screen',
      'Contemporary design aesthetic',
    ],
  },
  {
    id: 'support-facilities',
    name: 'Kitchen & Support Facilities',
    description: 'Fully equipped kitchen and pantry area, utility rooms, and secure storage spaces to support long production days and equipment management.',
    image: IMAGES.BIBIA_STUDIOS_KITCHEN_PANTRY_AREA_1_102,
    specifications: [
      'Full kitchen with appliances',
      'Pantry and refreshment area',
      'Utility and storage rooms',
      'Equipment prep areas',
      'Secure storage systems',
      'Production support infrastructure',
    ],
  },
];

export const equipment: Equipment[] = [
  {
    id: 'cinema-cameras',
    category: 'Camera Systems',
    name: 'Large-Format Digital Cinema Cameras',
    specifications: [
      '8K and beyond resolution capability',
      'Global shutter and high dynamic range capture',
      'Professional broadcast and cinema camera kits',
      'Multiple sensor format options',
      'RAW and ProRes recording formats',
      'Modular accessory systems',
    ],
    image: IMAGES.FILM_PRODUCTION_1,
  },
  {
    id: 'specialty-cameras',
    category: 'Camera Systems',
    name: 'High-Speed & Specialty Cameras',
    specifications: [
      'High-speed cameras for action sequences',
      'Aerial and drone cinematography systems',
      'Underwater camera housings',
      'Specialty rigs and mounts',
      'Remote camera control systems',
      'Multi-camera synchronization',
    ],
    image: IMAGES.FILM_PRODUCTION_2,
  },
  {
    id: 'cinema-lenses',
    category: 'Optics',
    name: 'Premium Cinema Lenses',
    specifications: [
      'Cinema prime lens sets',
      'Professional zoom lenses',
      'Large-format compatible glass',
      'Anamorphic lens options',
      'Specialty and vintage lenses',
      'Full focal length coverage',
    ],
    image: IMAGES.FILM_PRODUCTION_3,
  },
  {
    id: 'lighting-systems',
    category: 'Lighting & Grip',
    name: 'Advanced Lighting Systems',
    specifications: [
      'RGB and soft-light LED panels',
      'High-output daylight and HMI systems',
      'Studio and location lighting kits',
      'Wireless DMX control systems',
      'Lighting modifiers and diffusion',
      'Power distribution and generators',
    ],
    image: IMAGES.CINEMA_SCREEN_1,
  },
  {
    id: 'grip-equipment',
    category: 'Lighting & Grip',
    name: 'Grip & Rigging Support',
    specifications: [
      'Professional grip stands and arms',
      'Dolly and track systems',
      'Crane and jib arms',
      'Rigging and mounting hardware',
      'Stabilization systems',
      'Camera support accessories',
    ],
    image: IMAGES.FILM_REEL_1,
  },
  {
    id: 'post-hardware',
    category: 'Post-Production',
    name: 'Editing & Grading Workstations',
    specifications: [
      'High-performance editing workstations',
      'Dedicated color grading control panels',
      'Professional 4K/8K reference displays',
      'Calibrated monitoring systems',
      'Enterprise-grade storage with redundancy',
      'High-speed network infrastructure',
    ],
  },
  {
    id: 'audio-equipment',
    category: 'Sound',
    name: 'Professional Audio Systems',
    specifications: [
      'Professional studio monitors',
      'Audio mixing and recording equipment',
      'Microphone and boom systems',
      'Wireless audio transmission',
      'Sound design and foley tools',
      'Acoustic treatment systems',
    ],
  },
];

export const testimonials: Testimonial[] = [
  {
    id: 'testimonial-1',
    name: 'Rajesh Kumar',
    role: 'Director',
    company: 'Independent Filmmaker',
    quote: 'Bibia Films provided the perfect environment for our international co-production. The facilities are world-class, and the team understands the nuances of global storytelling. Their commitment to artistic integrity while maintaining technical excellence is unmatched.',
  },
  {
    id: 'testimonial-2',
    name: 'Sarah Mitchell',
    role: 'Executive Producer',
    company: 'Global Media Productions',
    quote: 'Working with Keshhav and the Bibia Films team was transformative. Their vision for future-facing cinema aligns perfectly with our goals. The post-production facilities and technical expertise helped us deliver a product that exceeded international standards.',
  },
  {
    id: 'testimonial-3',
    name: 'Amit Sharma',
    role: 'Cinematographer',
    company: 'Freelance DOP',
    quote: 'The equipment inventory at Bibia Films is impressive. From large-format cameras to premium lenses and lighting systems, everything is maintained to the highest standards. The shooting floor with chroma key capabilities gave us the flexibility we needed for complex VFX sequences.',
  },
  {
    id: 'testimonial-4',
    name: 'Elena Rodriguez',
    role: 'Producer',
    company: 'International Film Collective',
    quote: 'Bibia Films understands the complexities of international productions. Their ability to coordinate global crews and facilitate cross-cultural storytelling made our project seamless. The studio spaces are designed with both functionality and inspiration in mind.',
  },
  {
    id: 'testimonial-5',
    name: 'Michael Chen',
    role: 'Editor & Colorist',
    company: 'Post-Production Specialist',
    quote: 'The editing and color grading suites at Bibia Films are among the best I have worked in. The combination of high-performance workstations, calibrated displays, and professional control panels allowed us to achieve the exact look we envisioned. The technical support team is knowledgeable and responsive.',
  },
  {
    id: 'testimonial-6',
    name: 'Priya Desai',
    role: 'Creative Director',
    company: 'Brand Content Agency',
    quote: 'For our commercial production, Bibia Films delivered beyond expectations. The studio rental was flexible, the equipment was top-tier, and the creative consultation helped elevate our concept. Their understanding of both artistic vision and commercial requirements is rare.',
  },
];